import { CheckCircle, Target, Users, Award } from "lucide-react";

const About = () => {
  const values = [
    {
      icon: <Target size={32} />,
      title: "Mission-Driven",
      description:
        "Our mission is to empower businesses through innovative technology solutions that drive measurable results and sustainable growth.",
    },
    {
      icon: <CheckCircle size={32} />,
      title: "Quality First",
      description:
        "We maintain the highest standards of quality in every project, ensuring robust, scalable, and secure solutions.",
    },
    {
      icon: <Users size={32} />,
      title: "Client Success",
      description:
        "Your success is our success. We build long-term partnerships based on trust, transparency, and exceptional results.",
    },
    {
      icon: <Award size={32} />,
      title: "Excellence",
      description:
        "We continuously strive for excellence, staying ahead of technology trends and industry best practices.",
    },
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-blue-900 to-blue-700 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              About Zen Tech Services
            </h1>
            <p className="text-blue-100 text-lg md:text-xl max-w-3xl mx-auto">
              Leading the way in technology innovation and digital transformation
            </p>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
                Our Story
              </span>
              <h2 className="text-4xl font-bold text-gray-900 mt-4 mb-6">
                Building the Future of{" "}
                <span className="text-blue-600">Technology Solutions</span>
              </h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Zen Tech Services was founded with a vision to bridge the gap between cutting-edge technology and business success. We bring innovative approaches and proven expertise to solving complex technology challenges for businesses of all sizes.
                </p>
                <p>
                  Our expertise spans across algorithmic trading automation, multi-cloud infrastructure (AWS & Azure), contact center solutions, and custom application development using modern technologies like React and Java. We've successfully delivered transformative projects for clients across various industries, helping them achieve operational excellence and competitive advantages.
                </p>
                <p>
                  What sets us apart is our commitment to understanding your unique business challenges and delivering tailored solutions that drive real, measurable results. We don't just implement technology; we transform businesses with passion, expertise, and dedication.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-blue-600 to-blue-400 rounded-2xl opacity-20 blur-2xl" />
              <img
                src="https://images.unsplash.com/photo-1553877522-43269d4ea984"
                alt="Team working together"
                className="relative rounded-2xl shadow-2xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-blue-600 font-semibold text-sm uppercase tracking-wider">
              Our Values
            </span>
            <h2 className="text-4xl font-bold text-gray-900 mt-4">
              What Drives Us Forward
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-white p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2"
              >
                <div className="w-14 h-14 bg-blue-100 rounded-lg flex items-center justify-center mb-4 text-blue-600">
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900">
              Our Core Expertise
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Algorithmic Trading Automation
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                We design and implement sophisticated trading systems that leverage machine learning and quantitative analysis. Our solutions help financial institutions and traders optimize strategies, manage risk, and execute trades with precision.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>High-frequency trading systems</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Risk management algorithms</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Backtesting and optimization</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                AWS & Azure Cloud Solutions
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                From cloud migration to optimization, we provide comprehensive AWS and Azure services. Our certified experts design scalable, secure, and cost-effective multi-cloud architectures that power modern businesses.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Multi-cloud migration (AWS & Azure)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Infrastructure as Code (Terraform)</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>DevOps and CI/CD automation</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Contact Center Automation
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Transform customer experiences with our contact center solutions using Amazon Connect, Twilio, and SignalWire. We deliver intelligent, omnichannel communication platforms.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>IVR and call routing automation</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Real-time analytics</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Multi-channel integration</span>
                </li>
              </ul>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-white p-8 rounded-xl border border-blue-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-4">
                Web & Mobile Development
              </h3>
              <p className="text-gray-600 leading-relaxed mb-4">
                Build powerful web and iOS applications with modern technologies like React and Java. From concept to deployment, we create enterprise-grade applications that drive business growth.
              </p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>React web applications & SPAs</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Java Spring Boot microservices</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle size={18} className="text-blue-600 mr-2 flex-shrink-0 mt-0.5" />
                  <span>Native iOS development & RESTful APIs</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Let's Build Something Amazing Together
          </h2>
          <p className="text-blue-100 text-lg mb-8">
            Partner with us to transform your business through technology
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;
